import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Heart, Users, Wallet, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format } from 'date-fns';
import { toast } from 'sonner';
import ExportButton from '../components/ExportButton';
import { ChildFormDialog } from '../components/sponsorship/ChildFormDialog';
import { ChildExpenseFormDialog } from '../components/sponsorship/ChildExpenseFormDialog';
import { AddFundsDialog } from '../components/sponsorship/AddFundsDialog';
import { SponsorshipOverviewTab } from '../components/sponsorship/SponsorshipOverviewTab';
import { ChildrenTable, ChildExpensesTable } from '../components/sponsorship/SponsorshipTables';
import { SpendingSummaryTab, BulkBillTab } from '../components/sponsorship/SponsorshipSummaryTabs';
import { SponsorsTab, DonationsTab, ReportsBar } from '../components/sponsorship/SponsorshipProgramTabs';
import { AgreementsTab, BulkExpenseDialog, ProgramAlerts } from '../components/sponsorship/SponsorshipProgramExtras';
import { FinanceAssistant } from '../components/finance/FinanceAssistant';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CHILD_FORM = { name: '', date_of_birth: '', grade: '', school: '', sponsor_name: '', account_balance: 0, notes: '' };
const EMPTY_EXPENSE_FORM = { child_id: '', expense_type: '', amount: '', date: new Date(), description: '', custom_type_name: '' };

const SUMMARY_CARDS = [
  { key: 'children', icon: Users, bg: 'bg-pink-50 dark:bg-pink-900/20', color: 'text-pink-600', label: 'Total Children' },
  { key: 'balance', icon: Wallet, bg: 'bg-green-50 dark:bg-green-900/20', color: 'text-green-600', label: 'Total Balance' },
  { key: 'expenses', icon: TrendingDown, bg: 'bg-red-50 dark:bg-red-900/20', color: 'text-red-600', label: 'Total Expenses' },
  { key: 'avg', icon: Heart, bg: 'bg-purple-50 dark:bg-purple-900/20', color: 'text-purple-600', label: 'Avg per Child' },
];

const ChildSponsorshipPage = () => {
  const { hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [children, setChildren] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);

  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [fundsDialogOpen, setFundsDialogOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [deleteChildTarget, setDeleteChildTarget] = useState(null);
  const [deleteExpenseTarget, setDeleteExpenseTarget] = useState(null);

  const [childForm, setChildForm] = useState(EMPTY_CHILD_FORM);
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE_FORM);
  const [fundsForm, setFundsForm] = useState({ amount: '', description: '' });

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, childrenRes, expensesRes] = await Promise.all([
        axios.get(`${API_URL}/api/sponsorship/summary`),
        axios.get(`${API_URL}/api/sponsorship/children`),
        axios.get(`${API_URL}/api/sponsorship/expenses`),
      ]);
      setSummary(summaryRes.data);
      setChildren(childrenRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      toast.error('Failed to load sponsorship data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChildSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingChild) {
        await axios.put(`${API_URL}/api/sponsorship/children/${editingChild.id}`, childForm);
        toast.success('Child updated successfully');
      } else {
        await axios.post(`${API_URL}/api/sponsorship/children`, childForm);
        toast.success('Child registered successfully');
      }
      setChildDialogOpen(false);
      setEditingChild(null);
      setChildForm(EMPTY_CHILD_FORM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save child');
    }
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        date: format(expenseForm.date, 'yyyy-MM-dd'),
      };
      if (editingExpense) {
        const { child_id, ...updates } = payload;
        await axios.put(`${API_URL}/api/sponsorship/expenses/${editingExpense.id}`, updates);
        toast.success("Expense updated — child's balance adjusted");
      } else {
        await axios.post(`${API_URL}/api/sponsorship/expenses`, payload);
        toast.success('Expense recorded successfully');
      }
      setExpenseDialogOpen(false);
      setEditingExpense(null);
      setExpenseForm(EMPTY_EXPENSE_FORM);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleAddFunds = async (e) => {
    e.preventDefault();
    if (!selectedChild) return;
    try {
      await axios.post(`${API_URL}/api/sponsorship/children/${selectedChild.id}/add-funds?amount=${parseFloat(fundsForm.amount)}&description=${encodeURIComponent(fundsForm.description || '')}`);
      toast.success('Funds added successfully');
      setFundsDialogOpen(false);
      setFundsForm({ amount: '', description: '' });
      setSelectedChild(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add funds');
    }
  };

  const handleDeleteChild = async (childId) => {
    try {
      await axios.delete(`${API_URL}/api/sponsorship/children/${childId}`);
      toast.success('Child deleted');
      setDeleteChildTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete child');
    }
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${API_URL}/api/sponsorship/expenses/${expenseId}`);
      toast.success("Expense deleted — amount restored to child's balance");
      setDeleteExpenseTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete expense');
    }
  };

  const openEditChild = (child) => {
    setEditingChild(child);
    setChildForm({
      name: child.name,
      date_of_birth: child.date_of_birth || '',
      grade: child.grade || '',
      school: child.school || '',
      sponsor_name: child.sponsor_name || '',
      account_balance: child.account_balance || 0,
      notes: child.notes || '',
    });
    setChildDialogOpen(true);
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      child_id: expense.child_id,
      expense_type: expense.expense_type || '',
      amount: String(expense.amount ?? ''),
      date: expense.date ? new Date(expense.date) : new Date(),
      description: expense.description || '',
      custom_type_name: expense.custom_type_name || '',
    });
    setExpenseDialogOpen(true);
  };

  const openAddFunds = (child) => {
    setSelectedChild(child);
    setFundsDialogOpen(true);
  };

  const canEdit = hasRole(['admin', 'project_manager', 'finance_officer']);

  const summaryValues = {
    children: { value: summary?.total_children || 0, sub: `${summary?.active_children || 0} active` },
    balance: { value: formatCurrency(summary?.total_balance || 0) },
    expenses: { value: formatCurrency(summary?.total_expenses || 0) },
    avg: { value: formatCurrency((summary?.total_expenses || 0) / (summary?.total_children || 1)) },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Hero */}
      <div
        className="rounded-xl bg-cover bg-center relative overflow-hidden h-48"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=1200')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/90 to-purple-600/70" />
        <div className="absolute inset-0 flex items-center p-8">
          <div className="text-white">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="h-8 w-8" />
              <h2 className="font-heading text-3xl font-bold">Child Sponsorship Program</h2>
            </div>
            <p className="text-white/90 max-w-2xl">
              Supporting children's education, health, and welfare through dedicated sponsorships.
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sponsorship-summary">
        {SUMMARY_CARDS.map(({ key, icon: Icon, bg, color, label }) => (
          <Card key={key} className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${bg}`}>
                  <Icon className={`h-6 w-6 ${color}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className={`text-2xl font-heading font-bold ${key === 'balance' ? 'text-green-600' : key === 'expenses' ? 'text-red-600' : ''}`}>
                    {summaryValues[key].value}
                  </p>
                  {summaryValues[key].sub && <p className="text-xs text-pink-600">{summaryValues[key].sub}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview">Dashboard</TabsTrigger>
            <TabsTrigger value="children">Children</TabsTrigger>
            <TabsTrigger value="sponsors" data-testid="tab-sponsors">Sponsors</TabsTrigger>
            <TabsTrigger value="agreements" data-testid="tab-agreements">Agreements</TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
            <TabsTrigger value="expenses">Expenses</TabsTrigger>
            <TabsTrigger value="spending" data-testid="tab-spending">Per-Child Spending</TabsTrigger>
            <TabsTrigger value="bulk-bill" data-testid="tab-bulk-bill">Bulk Bill</TabsTrigger>
          </TabsList>

          {canEdit && (
            <div className="flex gap-2 flex-wrap">
              <ReportsBar />
              <BulkExpenseDialog children={children} onDone={fetchData} />
              <ExportButton path="exports/list/sponsored-children" filenameBase="suubi_sponsored_children" label="Export Children" testId="children-export-btn" />
              <ExportButton path="exports/list/sponsorship-expenses" filenameBase="suubi_sponsorship_expenses" label="Export Expenses" testId="sponsorship-expenses-export-btn" />
              <ChildFormDialog
                open={childDialogOpen}
                onOpenChange={(open) => { setChildDialogOpen(open); if (!open) setEditingChild(null); }}
                form={childForm}
                setForm={setChildForm}
                onSubmit={handleChildSubmit}
                editing={editingChild}
              />
              <ChildExpenseFormDialog
                open={expenseDialogOpen}
                onOpenChange={(o) => {
                  setExpenseDialogOpen(o);
                  if (!o) { setEditingExpense(null); setExpenseForm(EMPTY_EXPENSE_FORM); }
                }}
                form={expenseForm}
                setForm={setExpenseForm}
                onSubmit={handleExpenseSubmit}
                editing={editingExpense}
                children={children}
              />
            </div>
          )}
        </div>

        <TabsContent value="overview" className="space-y-6 mt-6">
          <ProgramAlerts />
          <SponsorshipOverviewTab summary={summary} />
        </TabsContent>

        <TabsContent value="sponsors" className="mt-6">
          <SponsorsTab refreshSignal={children.length} />
        </TabsContent>

        <TabsContent value="agreements" className="mt-6">
          <AgreementsTab onImported={fetchData} />
        </TabsContent>

        <TabsContent value="donations" className="mt-6">
          <DonationsTab children={children} onChanged={fetchData} />
        </TabsContent>

        <TabsContent value="children" className="mt-6">
          <ChildrenTable
            children={children}
            canEdit={canEdit}
            onAddFunds={openAddFunds}
            onEdit={openEditChild}
            onDelete={setDeleteChildTarget}
          />
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
          <ChildExpensesTable
            expenses={expenses}
            canEdit={canEdit}
            onEdit={openEditExpense}
            onDelete={setDeleteExpenseTarget}
          />
        </TabsContent>

        <TabsContent value="spending" className="mt-6">
          <SpendingSummaryTab children={children} expenses={expenses} />
        </TabsContent>

        <TabsContent value="bulk-bill" className="mt-6">
          <BulkBillTab expenses={expenses} />
        </TabsContent>
      </Tabs>

      <FinanceAssistant
        endpoint="/api/sponsorship-program/assistant"
        title="AI Sponsorship Assistant"
        description="Ask about sponsors, donations, allocations and child expenses"
        suggestions={[
          'Which sponsors have paid this year?',
          'Which children have the lowest remaining balances?',
          'How much sponsorship money is still unallocated?',
          'How much did we spend on uniforms?',
        ]}
      />

      <AddFundsDialog
        open={fundsDialogOpen}
        onOpenChange={setFundsDialogOpen}
        child={selectedChild}
        form={fundsForm}
        setForm={setFundsForm}
        onSubmit={handleAddFunds}
      />

      {/* Delete confirmations */}
      <AlertDialog open={!!deleteChildTarget} onOpenChange={(o) => { if (!o) setDeleteChildTarget(null); }}>
        <AlertDialogContent data-testid="delete-child-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sponsored child?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteChildTarget ? (
                <>Delete <strong>{deleteChildTarget.name}</strong> and ALL their related expenses? This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteChildTarget && handleDeleteChild(deleteChildTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-child-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteExpenseTarget} onOpenChange={(o) => { if (!o) setDeleteExpenseTarget(null); }}>
        <AlertDialogContent data-testid="delete-child-expense-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteExpenseTarget ? (
                <>Delete <strong>{formatCurrency(deleteExpenseTarget.amount)}</strong> ({deleteExpenseTarget.custom_type_name || deleteExpenseTarget.expense_type?.replace('_', ' ')}) for <strong>{deleteExpenseTarget.child_name}</strong>? The amount will be restored to the child's balance.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteExpenseTarget && handleDeleteExpense(deleteExpenseTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-child-expense-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ChildSponsorshipPage;
